import React, { useState, useEffect } from 'react';
import { FiTrash2, FiCornerUpLeft, FiCornerUpRight, FiPrinter, FiArrowLeft, FiDownload, FiPaperclip, FiEye, FiX, FiStar } from 'react-icons/fi';
import ForwardModal from '../modal/ForwardModal';
import MessagingService from '../services/MessagingService';
import { authAPI } from '../api/api';
import '../css/Messages.css';
import { toast } from 'react-toastify';

const MessageView = ({ message, currentFolder, onBack, onDelete, onReply, users, onRefresh, getMessageAttachments, onToggleFavorite }) => {
  const [isForwardModalOpen, setIsForwardModalOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [showAttachmentViewer, setShowAttachmentViewer] = useState(false);

  // Fetch attachments when message changes
  useEffect(() => {
    if (message?.id && getMessageAttachments) {
      loadAttachments();
    }
  }, [message?.id, getMessageAttachments]);

  const loadAttachments = async () => {
    if (!message?.id || !getMessageAttachments) return;
    
    try {
      setLoadingAttachments(true);
     
      
      // First check if attachments are already in the message object
      if (message.attachments && Array.isArray(message.attachments) && message.attachments.length > 0) {
       
        setAttachments(message.attachments);
        return;
      }
      
      // If not, try to fetch them
    
      const attachmentList = await getMessageAttachments(message.id);
     
      setAttachments(attachmentList);
    } catch (error) {
    
      toast.error('Hiba a csatolmányok betöltésekor');
    } finally {
      setLoadingAttachments(false);
    
    }
  };

  if (!message) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('hu-HU');
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      'low': 'Alacsony',
      'normal': 'Normál',
      'high': 'Magas',
      'urgent': 'Sürgős'
    };
    return labels[priority] || priority;
  };

  const getPriorityClass = (priority) => {
    return `priority-${priority || 'normal'}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDownload = async (attachment) => {
    try {
      if (attachment.id) {
        // Konkrét csatolmány letöltése
        const link = document.createElement('a');
        link.href = `/api/messages/download-attachment/${attachment.id}`;
        link.download = attachment.filename || attachment.original_filename;
        link.style.display = 'none';
        
        // Add client user ID header if available
        const currentUserId = authAPI.getCurrentUserId && authAPI.getCurrentUserId();
        if (currentUserId) {

          const token = localStorage.getItem('authToken');
          const response = await fetch(`/api/messages/download-attachment/${attachment.id}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            link.href = url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } else {
            toast.error('Hiba a letöltés során');
          }
        } else {

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (error) {
     
      toast.error('Hiba a letöltés során');
    }
  };

  const handleViewAttachment = (attachment) => {
    setSelectedAttachment(attachment);
    setShowAttachmentViewer(true);
  };

  const closeAttachmentViewer = () => {
    setShowAttachmentViewer(false);
    setSelectedAttachment(null);
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return '🖼️';
    if (mimeType?.startsWith('application/pdf')) return '📄';
    if (mimeType?.startsWith('application/msword') || mimeType?.includes('wordprocessingml')) return '📝';
    if (mimeType?.startsWith('application/vnd.ms-excel') || mimeType?.includes('spreadsheetml')) return '📊';
    if (mimeType?.startsWith('text/')) return '📄';
    return '📎';
  };

  const renderAttachments = () => {
    if (loadingAttachments) {
      return (
        <div className="messageview-attachments">
          <h4><FiPaperclip /> Mellékletek</h4>
          <div className="attachments-loading">Csatolmányok betöltése...</div>
        </div>
      );
    }

    if (!attachments || attachments.length === 0) {
      return null;
    }

    return (
      <div className="messageview-attachments">
        <h4><FiPaperclip /> Mellékletek ({attachments.length})</h4>
        <div className="attachments-list">
          {attachments.map((attachment, index) => (
            <div key={index} className="attachment-item">
              <div className="attachment-info">
                <span className="attachment-icon">{getFileIcon(attachment.mime_type)}</span>
                <span className="attachment-name">{attachment.filename || attachment.original_filename}</span>
                <span className="attachment-size">{formatFileSize(attachment.file_size || attachment.size)}</span>
                <span className="attachment-type">{attachment.mime_type}</span>
              </div>
              <div className="attachment-actions">
                <button 
                  className="attachment-download"
                  onClick={() => handleDownload(attachment)}
                  title="Csatolmány letöltése"
                >
                  <FiDownload size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAttachmentViewer = () => {
    if (!showAttachmentViewer || !selectedAttachment) return null;

    const isImage = selectedAttachment.mime_type?.startsWith('image/');
    const isPDF = selectedAttachment.mime_type === 'application/pdf';
    const isText = selectedAttachment.mime_type?.startsWith('text/');

    return (
      <div className="attachment-viewer-overlay" onClick={closeAttachmentViewer}>
        <div className="attachment-viewer-content" onClick={e => e.stopPropagation()}>
          <div className="attachment-viewer-header">
            <h3>{selectedAttachment.filename || selectedAttachment.original_filename}</h3>
            <button className="attachment-viewer-close" onClick={closeAttachmentViewer}>
              <FiX />
            </button>
          </div>
          <div className="attachment-viewer-body">
            {isImage ? (
              <img 
                src={`/api/messages/download-attachment/${selectedAttachment.id}`} 
                alt={selectedAttachment.filename || 'Csatolmány'}
                style={{ maxWidth: '100%', maxHeight: '70vh' }}
              />
            ) : isPDF ? (
              <iframe
                src={`/api/messages/download-attachment/${selectedAttachment.id}`}
                width="100%"
                height="70vh"
                title="PDF megtekintő"
              />
            ) : isText ? (
              <pre className="text-attachment-content">
                {/* Text content would need to be fetched separately */}
                <p>Szöveges csatolmány megtekintése nem támogatott. Kérjük, töltse le a fájlt.</p>
              </pre>
            ) : (
              <div className="unsupported-attachment">
                <p>Ez a fájltípus nem tekinthető meg közvetlenül.</p>
                <p>Kérjük, töltse le a fájlt a megtekintéshez.</p>
                <button 
                  className="download-button"
                  onClick={() => handleDownload(selectedAttachment)}
                >
                  <FiDownload /> Letöltés
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handlePrint = () => {
    // Nyomtatási tartalom létrehozása
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${message.subject || 'Üzenet'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .subject { font-size: 18px; font-weight: bold; margin-bottom: 15px; }
            .meta { margin-bottom: 20px; }
            .meta-row { margin-bottom: 8px; }
            .label { font-weight: bold; }
            .content { line-height: 1.6; }
            .attachments { margin-top: 20px; border-top: 1px solid #ccc; padding-top: 15px; }
            .attachment-item { margin-bottom: 10px; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="subject">${message.subject || 'Nincs tárgy'}</div>
          </div>
          
          <div class="meta">
            <div class="meta-row">
              <span class="label">Feladó:</span> 
              ${typeof message.from === 'object' && message.from.name 
                ? `${message.from.name} (${message.from.email})` 
                : (message.from || 'Ismeretlen')}
            </div>
            <div class="meta-row">
              <span class="label">Címzettek:</span> 
              ${message.to && message.to.length > 0 
                ? message.to.map(recipient => 
                    typeof recipient === 'object' 
                      ? (recipient.name || recipient.email) 
                      : recipient
                  ).join(', ')
                : 'Nincs címzett'
              }
            </div>
            <div class="meta-row">
              <span class="label">Dátum:</span> 
              ${formatDate(message.date || message.created_at)}
            </div>
            <div class="meta-row">
              <span class="label">Prioritás:</span> 
              ${getPriorityLabel(message.priority)}
            </div>
          </div>
          
          <div class="content">
            ${message.content || 'Nincs tartalom'}
          </div>
          
          ${message.attachments && message.attachments.length > 0 ? `
            <div class="attachments">
              <h4>Mellékletek (${message.attachments.length})</h4>
              ${message.attachments.map(attachment => `
                <div class="attachment-item">
                  <strong>${attachment.filename || attachment.original_filename}</strong>
                  <br>
                  <small>${formatFileSize(attachment.file_size || attachment.size)} • ${attachment.mime_type}</small>
                </div>
              `).join('')}
            </div>
          ` : ''}
        </body>
      </html>
    `;
    
    const printFrame = document.createElement('iframe');
    printFrame.style.display = 'none';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(printContent);
    printFrame.contentDocument.close();
    
    printFrame.contentWindow.print();
    
    setTimeout(() => {
      document.body.removeChild(printFrame);
    }, 1000);
  };

  const handleForward = () => {
    setIsForwardModalOpen(true);
  };

  const handleReply = () => {
    if (onReply) {
      onReply();
    } else {
      // Fallback: show toast message
      toast.info('Válasz funkció fejlesztés alatt. Kérjük, használja a "Továbbítás" funkciót.');
    }
  };

  const handleForwardSubmit = async (recipients, copyAttachments = true) => {
    try {

      
      const forwardMessage = {
        subject: `Fwd: ${message.subject || 'Üzenet'}`,
        content: `
Eredeti üzenet:
Feladó: ${typeof message.from === 'object' && message.from.name 
  ? `${message.from.name} (${message.from.email})` 
  : (message.from || 'Ismeretlen')}
Dátum: ${formatDate(message.date || message.created_at)}
Tárgy: ${message.subject || 'Nincs tárgy'}

${message.content || 'Nincs tartalom'}

${message.attachments && message.attachments.length > 0 
  ? `\nMellékletek: ${message.attachments.length} db` 
  : ''}
        `.trim(),
        recipients: recipients,
        priority: message.priority || 'normal',
        copyAttachments: copyAttachments 
      };

      const messagingService = new MessagingService();
      
     
      if (window.clientUserId) {
        messagingService.setClientUserId(window.clientUserId);
      }
      
      const result = await messagingService.forwardMessage(message.id, forwardMessage);
      
      if (result.success) {
        toast.success(`Üzenet sikeresen továbbítva ${recipients.length} címzettnek!`);
        
        // Opcionális: frissítsd a mappa statisztikákat
        // window.location.reload(); // vagy más frissítési logika
        onRefresh && onRefresh(); 
      } else {
        toast.error(` Hiba a továbbítás során: ${result.message}`);
      }
      
    } catch (error) {
     
      toast.error(` Hiba a továbbítás során: ${error.message}`);
    }
  };



  return (
    <div className="messageview-card">
      <div className="messageview-header-row">
        <div className="messageview-header-title">Levél Olvasása</div>
        <button className="messageview-back-btn" onClick={onBack}>
          <FiArrowLeft style={{marginRight:6, verticalAlign:'text-top'}} />
          Vissza
        </button>
      </div>
      
      <div className="messageview-subject-row">
        <div className="messageview-subject">{message.subject || 'Nincs tárgy'}</div>
        <div className="messageview-date">{formatDate(message.date || message.created_at)}</div>
      </div>
      
      <div className="messageview-meta">
        <div className="messageview-sender">
          <strong>Feladó:</strong> {
            typeof message.from === 'object' && message.from.name 
              ? `${message.from.name} (${message.from.email})` 
              : (message.from || 'Ismeretlen')
          }
        </div>
        <div className="messageview-recipients">
          <strong>Címzettek:</strong>
          {message.to && message.to.length > 0 ? (
            <div className="recipients-list">
              {message.to.map((recipient, index) => {
                
                if (typeof recipient === 'string') {
                  return (
                    <span key={index} className="recipient-item">
                      {recipient}
                      {index < message.to.length - 1 ? ', ' : ''}
                    </span>
                  );
                } else {
                  return (
                    <span key={index} className="recipient-item">
                      {recipient.name || recipient.email}
                      {index < message.to.length - 1 ? ', ' : ''}
                    </span>
                  );
                }
              })}
            </div>
          ) : (
            'Nincs címzett'
          )}
        </div>
        <div className="messageview-priority">
          <strong>Prioritás:</strong>
          <span className={getPriorityClass(message.priority)}>
            {getPriorityLabel(message.priority)}
          </span>
        </div>
        {message.updated_at && message.updated_at !== (message.date || message.created_at) && (
          <div className="messageview-updated">
            <strong>Módosítva:</strong> {formatDate(message.updated_at)}
          </div>
        )}
      </div>
      
      <div className="messageview-actions-row">
        <button 
          className="messageview-action" 
          onClick={() => onDelete && onDelete(message.id)}
          title="Törlés"
        >
          <FiTrash2 />
        </button>
        {/* Ne jelenjen meg a csillag ikon, ha az üzenet már a kedvencek mappában van */}
        {currentFolder !== 'favorites' && (
          <button 
            className={`messageview-action ${message.is_favorite ? 'favorite-active' : ''}`}
            onClick={() => onToggleFavorite && onToggleFavorite(message.id)}
            title={message.is_favorite ? 'Kedvencekből eltávolítás' : 'Kedvencekhez adás'}
          >
            <FiStar style={{ color: message.is_favorite ? '#ffd700' : 'var(--color-text-gray)' }} />
          </button>
        )}
        {currentFolder !== 'sent' && currentFolder !== 'favorites' && (
          <button className="messageview-action" title="Válasz" onClick={handleReply}>
            <FiCornerUpLeft />
          </button>
        )}
        <button className="messageview-action" title="Továbbítás" onClick={handleForward}>
          <FiCornerUpRight />
        </button>
        <button className="messageview-action" title="Nyomtatás" onClick={handlePrint}>
          <FiPrinter />
        </button>
      </div>
      
      <div className="messageview-content">
        {message.content || 'Nincs tartalom'}
      </div>
      
      {renderAttachments()}
      {renderAttachmentViewer()}
      
      <div className="messageview-footer-row">
        <button 
          className="messageview-footer-btn danger"
          onClick={() => onDelete && onDelete(message.id)}
        >
          <FiTrash2 style={{marginRight:4}} />
          Törlés
        </button>
        <button className="messageview-footer-btn" onClick={handlePrint}>
          <FiPrinter style={{marginRight:4}} />
          Nyomtatás
        </button>
        <span style={{flex:1}}></span>
        {currentFolder !== 'sent' && currentFolder !== 'favorites' && (
          <button className="messageview-footer-btn" onClick={handleReply}>
            <FiCornerUpLeft style={{marginRight:4}} />
            Válasz
          </button>
        )}
        <button className="messageview-footer-btn" onClick={handleForward}>
          <FiCornerUpRight style={{marginRight:4}} />
          Továbbítás
        </button>
      </div>
      
   
      <ForwardModal
        isOpen={isForwardModalOpen}
        onClose={() => setIsForwardModalOpen(false)}
        message={message}
        onForward={handleForwardSubmit}
        users={users}
      />
    </div>
  );
};

export default MessageView; 